import React from "react";
import {Button, Text, Stack, ButtonIcon, Spinner} from "@nike/podium";
import {Download, Tip} from "@nike/nike-design-system-icons";

type ImbalanceFooterProps = {
    lineCount: number;
    onExportWmsZero: () => void;
    onSuggestCleanups: () => void;
    currentPage: number;
    totalPages: number;
    rangeStart: number;
    rangeEnd: number;
    totalItems: number;
    hasMore: boolean;
    loadingMore: boolean;
    loadMoreError: string | null;
    reachedEnd: boolean;
    onPageChange: (page: number) => void;
};

export function ImbalanceFooter({
    lineCount,
    onExportWmsZero,
    onSuggestCleanups,
    currentPage,
    totalPages,
    rangeStart,
    rangeEnd,
    totalItems,
    hasMore,
    loadingMore,
    loadMoreError,
    reachedEnd,
    onPageChange,
}: ImbalanceFooterProps) {
    const isLastPage = currentPage >= totalPages;

    return (
        <div className="im-footer">
            <Stack horizontal gap="s" align="center">
                <Text font="body-4" color="secondary">
                    {lineCount} line{lineCount !== 1 ? 's' : ''}{hasMore ? '+' : ''} &middot; ELC
                </Text>
                <Button variant="outlined" size="s" onClick={onExportWmsZero}>
                    <ButtonIcon>
                        <Download size="s" />
                    </ButtonIcon>
                    Export WMS=0 list
                </Button>
            </Stack>

            <Stack horizontal gap="m" align="center">
                {totalItems > 0 && (
                    <div className="im-pagination">
                        <button
                            className="im-page-btn"
                            disabled={currentPage <= 1}
                            onClick={() => onPageChange(currentPage - 1)}
                            aria-label="Previous page"
                        >
                            ‹
                        </button>
                        <input
                            className="im-page-input"
                            type="text"
                            value={currentPage}
                            onChange={e => {
                                const val = parseInt(e.target.value, 10);
                                const maxAllowed = hasMore ? totalPages + 1 : totalPages;
                                if (!isNaN(val) && val >= 1 && val <= maxAllowed) {
                                    onPageChange(val);
                                }
                            }}
                            aria-label="Page number"
                        />
                        <button
                            className="im-page-btn"
                            disabled={isLastPage && !hasMore && !loadingMore}
                            onClick={() => onPageChange(currentPage + 1)}
                            aria-label="Next page"
                        >
                            ›
                        </button>

                        {loadingMore ? (
                            <span className="im-page-range im-page-range-loading">
                                <Spinner size="s" /> Loading more results…
                            </span>
                        ) : loadMoreError ? (
                            <span className="im-page-range im-page-range-error">
                                Failed to load more results
                            </span>
                        ) : reachedEnd ? (
                            <span className="im-page-range im-page-range-end">
                                No more results
                            </span>
                        ) : (
                            <Text font="body-4" color="secondary" as="span" className="im-page-range">
                                {rangeStart}–{rangeEnd} of {totalItems}{hasMore ? '+' : ''}
                            </Text>
                        )}
                    </div>
                )}

                <Button size="s" onClick={onSuggestCleanups} className="im-btn-accent">
                    <ButtonIcon>
                        <Tip size="s" />
                    </ButtonIcon>
                    Suggest adjustments
                </Button>
            </Stack>
        </div>
    );
}
